import { Component, OnInit } from '@angular/core';
import { DebugService } from '../services/debug.service';
import { AnalyticsService, AnalyticsEvent } from '../analytics/analytics.service';
import { FirebaseAuthService } from '../services/firebase-auth.service';
import { FirestoreStorageService, SyncAuditEntry } from '../services/firestore-storage.service';
import { SignupStateService } from '../services/signup-state.service';
import { SessionStorageService } from '../session-storage.service';
import { Router } from '@angular/router';

@Component({
  selector: 'app-debug-panel',
  templateUrl: './debug-panel.component.html',
  styleUrls: ['./debug-panel.component.css']
})
export class DebugPanelComponent implements OnInit {
  activeTab: 'storage' | 'analytics' | 'sync' | 'environment' = 'storage';
  isPanelOpen = false;

  // Analytics data
  analyticsEvents: AnalyticsEvent[] = [];
  analyticsDebugMode = false;

  // Firestore sync audit data (live-updating, newest first)
  syncAuditEntries: SyncAuditEntry[] = [];

  // Environment data
  environmentInfo: { key: string; value: string }[] = [];

  // Local storage modal visibility
  showLocalStorageModal = false;

  constructor(
    private debugService: DebugService,
    private analyticsService: AnalyticsService,
    private firebaseAuthService: FirebaseAuthService,
    private firestoreStorageService: FirestoreStorageService,
    private signupStateService: SignupStateService,
    private sessionStorageService: SessionStorageService,
    private router: Router
  ) {}

  ngOnInit(): void {
    // Subscribe to panel open state
    this.debugService.panelOpen$.subscribe(isOpen => {
      this.isPanelOpen = isOpen;
      if (isOpen) {
        this.refreshData();
      }
    });

    // Sync audit log updates live while the panel is open
    this.firestoreStorageService.getSyncAudit$().subscribe(entries => {
      this.syncAuditEntries = entries;
    });
  }

  /**
   * Refresh all debug data
   */
  refreshData(): void {
    this.refreshAnalyticsData();
    this.refreshEnvironmentData();
  }

  /**
   * Refresh analytics data
   */
  refreshAnalyticsData(): void {
    this.analyticsEvents = this.analyticsService.getRecentEvents();
    this.analyticsDebugMode = this.analyticsService.isDebugMode();
  }

  /**
   * Refresh environment data
   */
  refreshEnvironmentData(): void {
    this.environmentInfo = this.debugService.getEnvironmentInfo();
  }

  /**
   * Switch active tab
   */
  setActiveTab(tab: 'storage' | 'analytics' | 'sync' | 'environment'): void {
    this.activeTab = tab;
  }

  /**
   * Sync audit summary counts for the tab header
   */
  get syncAuditCounts(): { attempts: number; successes: number; failures: number; skipped: number } {
    const counts = { attempts: 0, successes: 0, failures: 0, skipped: 0 };
    for (const entry of this.syncAuditEntries) {
      if (entry.status === 'attempt') counts.attempts++;
      else if (entry.status === 'success') counts.successes++;
      else if (entry.status === 'failure') counts.failures++;
      else if (entry.status === 'skipped') counts.skipped++;
    }
    return counts;
  }

  /**
   * Clear the sync audit log
   */
  clearSyncAudit(): void {
    this.firestoreStorageService.clearSyncAudit();
  }

  /**
   * Tailwind classes for a sync audit status badge
   */
  syncStatusClass(status: SyncAuditEntry['status']): string {
    switch (status) {
      case 'success': return 'bg-green-100 text-green-800';
      case 'failure': return 'bg-red-100 text-red-800';
      case 'skipped': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-200 text-gray-700';
    }
  }

  /**
   * Compact field list for a sync audit entry
   */
  formatSyncFields(entry: SyncAuditEntry): string {
    if (!entry.fields.length) {
      return entry.operation === 'load' ? 'session' : entry.operation === 'delete' ? 'session doc' : '';
    }
    return entry.fields.join(', ');
  }

  /**
   * Close the debug panel
   */
  closePanel(): void {
    this.debugService.closePanel();
  }

  /**
   * Toggle analytics debug mode
   */
  toggleAnalyticsDebug(): void {
    this.analyticsService.setDebugMode(!this.analyticsDebugMode);
    this.analyticsDebugMode = this.analyticsService.isDebugMode();
  }

  /**
   * Clear analytics event history
   */
  clearAnalyticsHistory(): void {
    if (confirm('Are you sure you want to clear the analytics event history?')) {
      this.analyticsService.clearEventHistory();
      this.refreshAnalyticsData();
    }
  }

  /**
   * Open local storage modal
   */
  openLocalStorageModal(): void {
    this.showLocalStorageModal = true;
  }

  /**
   * Close local storage modal
   */
  closeLocalStorageModal(): void {
    this.showLocalStorageModal = false;
  }

  /**
   * Format timestamp for display
   */
  formatTimestamp(date: Date): string {
    const d = new Date(date);
    const hours = d.getHours().toString().padStart(2, '0');
    const minutes = d.getMinutes().toString().padStart(2, '0');
    const seconds = d.getSeconds().toString().padStart(2, '0');
    const ms = d.getMilliseconds().toString().padStart(3, '0');
    return `${hours}:${minutes}:${seconds}.${ms}`;
  }

  /**
   * Format event parameters for display
   */
  formatParameters(params: unknown): string {
    return JSON.stringify(params, null, 2);
  }

  /**
   * Reset user completely - clears all data and signs out
   */
  async resetUser(): Promise<void> {
    const currentUid = this.firebaseAuthService.getCurrentUserId();

    const confirmed = confirm(
      'Are you sure you want to reset the user?\n\n' +
      'This will:\n' +
      '• Clear all localStorage data\n' +
      '• Delete Firestore session data\n' +
      '• Sign out current anonymous user\n' +
      '• Create a NEW anonymous user (new UID)\n' +
      '• Redirect to login page\n\n' +
      `Current UID: ${currentUid || 'None'}\n\n` +
      'This action cannot be undone.'
    );

    if (!confirmed) {
      return;
    }

    try {
      console.log('=== Starting user reset ===');
      console.log('Current anonymous UID:', currentUid);

      // 1. Delete Firestore session data (cancels pending writes too)
      try {
        await this.signupStateService.clearSession();
        console.log('✓ Firestore session deleted');
      } catch (error) {
        console.warn('Failed to delete Firestore session:', error);
      }

      // 2. Sign out anonymous user
      try {
        await this.firebaseAuthService.signOutUser();
        console.log('✓ Anonymous user signed out');
      } catch (error) {
        console.warn('Failed to sign out user:', error);
      }

      // 3. Clear all localStorage (including anonymous UID)
      this.sessionStorageService.clearAllStorage();
      console.log('✓ localStorage cleared');

      // 4. Clear analytics history
      this.analyticsService.clearEventHistory();
      console.log('✓ Analytics history cleared');

      // 5. Create NEW anonymous user
      console.log('Creating new anonymous user...');
      const newUser = await this.firebaseAuthService.getOrCreateAnonymousUser();
      const newUid = newUser?.uid;
      console.log('✓ New anonymous user created:', newUid);

      // 6. Re-subscribe the signup state to the new user's session doc
      await this.signupStateService.initialize();
      console.log('✓ Signup state reinitialized');

      // 7. Close debug panel
      this.closePanel();

      // 8. Show success message with UID change
      alert(
        'User reset complete!\n\n' +
        `Old UID: ${currentUid || 'None'}\n` +
        `New UID: ${newUid || 'Failed to create'}\n\n` +
        'Redirecting to login page...'
      );

      // 9. Redirect to login and reload
      await this.router.navigate(['/login']);
      window.location.reload();

    } catch (error) {
      console.error('Error during user reset:', error);
      alert('An error occurred while resetting the user. Please try again.');
    }
  }
}
